<script setup lang="ts">
import { computed } from "vue";

interface FaqItem {
  q?: string;
  a?: string;
  question?: string;
  answer?: string;
}

const props = defineProps<{
  items: FaqItem[];
  id?: string;
}>();

const { t, rt } = useI18n();

// Normalize items to handle different translation structures and potential message objects
const normalizedItems = computed(() => {
  if (!props.items || !Array.isArray(props.items)) return [];

  return props.items.map((item) => {
    const qValue = item.q || item.question || "";
    const aValue = item.a || item.answer || "";

    return {
      question: typeof qValue === "string" ? qValue : rt(qValue),
      answer: typeof aValue === "string" ? aValue : rt(aValue),
    };
  });
});
</script>

<template>
  <div :id="id" class="w-full space-y-4">
    <details
      v-for="(item, index) in normalizedItems"
      :key="index"
      class="group bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm transition-all duration-300 hover:shadow-md"
    >
      <summary
        class="flex items-center justify-between p-6 md:p-8 cursor-pointer list-none font-bold text-lg md:text-xl text-gray-900 dark:text-white xpirience-faq-summary"
      >
        <span class="pr-4">{{ item.question }}</span>
        <div
          class="w-8 h-8 shrink-0 rounded-full border-2 border-primary/20 flex items-center justify-center text-primary group-open:rotate-180 transition-transform duration-300"
        >
          <Icon name="ph:caret-down-bold" class="w-4 h-4" />
        </div>
      </summary>
      <div
        class="px-6 md:px-8 pb-6 md:pb-8 text-gray-600 dark:text-gray-300 leading-relaxed text-base md:text-lg whitespace-pre-line xpirience-faq-answer"
      >
        <slot :name="`answer-${index}`" :item="item">
          {{ item.answer }}
        </slot>
      </div>
    </details>
  </div>
</template>

<style scoped>
/* Hide default summary arrow */
summary::-webkit-details-marker {
  display: none;
}

summary {
  list-style: none;
}

.xpirience-faq-summary {
  outline: none;
}

/* Smooth transition for details - although details/summary doesn't support easy height transition without JS, 
   we can at least make the opacity feel smooth if we wanted. 
   But keeping it standard for now. */
</style>
